import type { EntityAccess, EntityAccessLevel, PermissionsType } from '../types/types';

export type EntityTreeNode = EntityAccess & {
  children?: EntityTreeNode[];
};

type PermissionValue = EntityAccessLevel;

type PermissionRecord = Record<string, PermissionValue>;

type PermissionEstate = {
  id: string;
  name: string;
  inherited?: boolean;
  permissions: PermissionRecord;
};

type PermissionProject = {
  id: string;
  name: string;
  permissions: PermissionRecord;
  estates: PermissionEstate[];
};

export type PermissionsResponse = {
  projects: PermissionProject[];
  estates: PermissionEstate[];
};

type TenantEntityTree = {
  id: string;
  name: string;
  estates?: {
    id: string;
    name: string | null;
    projectId?: string | null;
  }[];
};

type TenantProject = {
  id: string;
  name: string | null;
};

type BuildTreeNodesProps = {
  permissions?: PermissionsResponse;
  tenant?: TenantEntityTree;
  projects?: TenantProject[];
};

export const buildTreeNodes = ({ permissions, tenant, projects }: BuildTreeNodesProps) => {
  if (!tenant) return [];

  const projectPermissions = new Map(permissions?.projects.map((project) => [project.id, project]));
  const estatePermissions = new Map<string, PermissionEstate>();
  const tenantProjects = projects ?? [];
  const tenantEstates = tenant.estates ?? [];

  permissions?.estates.forEach((estate) => {
    estatePermissions.set(estate.id, estate);
  });

  permissions?.projects.forEach((project) => {
    project.estates.forEach((estate) => {
      if (!estate.inherited) {
        estatePermissions.set(estate.id, estate);
      }
    });
  });

  const estatesByProjectId = new Map<string, TenantEntityTree['estates']>();
  const standaloneEstates: NonNullable<TenantEntityTree['estates']> = [];

  tenantEstates.forEach((estate) => {
    if (!estate.projectId) {
      standaloneEstates.push(estate);
      return;
    }

    const projectEstates = estatesByProjectId.get(estate.projectId) ?? [];
    projectEstates.push(estate);
    estatesByProjectId.set(estate.projectId, projectEstates);
  });

  return sortTreeNodes([
    ...tenantProjects.map(
      (project) =>
        ({
          id: project.id,
          name: project.name || project.id,
          type: 'project',
          access: getGeneralAccessLevel(projectPermissions.get(project.id)?.permissions),
          children: (estatesByProjectId.get(project.id) ?? []).map((estate) =>
            getEstateNode(estate, estatePermissions, projectPermissions.get(project.id))
          ),
          permissions: getPermissions(projectPermissions.get(project.id)?.permissions),
        }) satisfies EntityTreeNode
    ),
    ...standaloneEstates.map((estate) => getEstateNode(estate, estatePermissions)),
  ]);
};

export const sortTreeNodes = (nodes: EntityTreeNode[]): EntityTreeNode[] => {
  return [...nodes]
    .map((node) => {
      if (node.children?.length) {
        return {
          ...node,
          children: sortTreeNodes(node.children),
        };
      }
      return node;
    })
    .sort((a, b) => {
      const aHasAccess = a.access !== 'none';
      const bHasAccess = b.access !== 'none';

      if (aHasAccess && !bHasAccess) return -1;
      if (!aHasAccess && bHasAccess) return 1;

      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
};

const getEstateNode = (
  estate: NonNullable<TenantEntityTree['estates']>[number],
  estatePermissions: Map<string, PermissionEstate>,
  projectPermission?: PermissionProject
) => {
  const directPermission = estatePermissions.get(estate.id);
  const permission = directPermission?.permissions ?? projectPermission?.permissions;

  return {
    id: estate.id,
    name: directPermission?.name || estate.name || estate.id,
    type: 'estate',
    access: getGeneralAccessLevel(permission),
    permissions: getPermissions(permission),
  } satisfies EntityTreeNode;
};

export const filterTreeNodes = (
  nodes: EntityTreeNode[],
  normalizedSearch: string
): EntityTreeNode[] => {
  return nodes.flatMap((node) => {
    const children = node.children ? filterTreeNodes(node.children, normalizedSearch) : undefined;

    if (node.name.toLowerCase().includes(normalizedSearch) || children?.length) {
      return [
        {
          ...node,
          children,
        },
      ];
    }

    return [];
  });
};

const getGeneralAccessLevel = (permissions?: PermissionRecord): EntityAccessLevel =>
  getPermissionValue(permissions?.general);

const getPermissions = (permissions?: PermissionRecord) =>
  ({
    general: getPermissionValue(permissions?.general),
    media: getPermissionValue(permissions?.media),
    documentsInternal: getPermissionValue(permissions?.documentsInternal),
    documentsExternal: getPermissionValue(permissions?.documentsExternal),
  }) satisfies PermissionsType;

const getPermissionValue = (permission?: PermissionValue): EntityAccessLevel => {
  if (permission === 'write') return 'write';
  if (permission === 'read') return 'read';

  return 'none';
};
